import { ProdutoListaPrecoVigenteStruct } from '../../../../../shared/services/structs/produto/produto-lista-preco-vigente.struct';

/**
 * De onde saiu o preço da linha. É o que o popover do item (§6.2.5) explica em texto, e o que
 * decide se o item deve ser recalculado quando a lista do cabeçalho muda.
 */
export type OrigemPreco =
    /** A lista escolhida no cabeçalho do documento alcançou o produto e prevaleceu. */
    | 'cabecalho'
    /** O documento não tem lista no cabeçalho, e valeu a PADRÃO — o preço "de tabela". */
    | 'padrao'
    /** A regra automática escolheu outra lista — promocional mais barata, ou a única que alcança. */
    | 'automatica'
    /** O usuário escolheu a lista naquele item. Nunca é recalculada por troca no cabeçalho. */
    | 'manual'
    /** Nenhuma lista vigente alcança o produto: o item entra com preço zero e fica pendente. */
    | 'sem-lista';

export interface PrecoResolvido {
    lista: ProdutoListaPrecoVigenteStruct | null;
    origem: OrigemPreco;
}

/**
 * Escolhe, entre as listas vigentes que alcançam o produto, a que define o preço do item (§6.2.2.3).
 *
 * Mora no front porque é aqui que estão as três coisas de que a regra depende: as candidatas (que
 * vieram com o produto), a lista do cabeçalho e a configuração "Priorizar menor preço". E o
 * resultado é GRAVADO no item (`precoBrutoUnitario`, `precoLiquidoUnitario`, `idListaPreco`), então o servidor
 * persiste o que a tela decidiu em vez de reexecutar a regra — inclusive na conversão orçamento →
 * pedido, que por definição não recalcula preço (§12.5).
 *
 * @param listas       as vigentes que alcançam o produto, já ordenadas com a padrão à frente
 * @param idListaCabecalho  a lista escolhida no cabeçalho; nula quando o documento não tem uma
 * @param priorizarMenorPreco  Configurações ▸ Vendas ▸ Gerais
 */
export function resolverPrecoDoProduto(
    listas: ProdutoListaPrecoVigenteStruct[] | null | undefined,
    idListaCabecalho: number | null,
    priorizarMenorPreco: boolean,
): PrecoResolvido {
    const candidatas = listas ?? [];

    if (!candidatas.length) return { lista: null, origem: 'sem-lista' };

    // A base é a lista do cabeçalho. Se ela não alcança o produto — ou se o documento não tem
    // lista escolhida —, a padrão assume esse papel: é o preço "de tabela" do produto.
    const base = candidatas.find((l) => l.idListaPreco === idListaCabecalho)
        ?? candidatas.find((l) => l.isPadrao)
        ?? null;

    if (!base) {
        // O produto é alcançado só por listas que não são nem a do cabeçalho nem a padrão (uma
        // promoção, tipicamente). Deixá-lo sem preço seria pior do que aplicar a que existe, e a
        // §6.2.2.3 diz isso: "o item não fica sem preço". Na dúvida, a mais barata.
        const maisBarata = candidatas.reduce((a, b) => (b.preco < a.preco ? b : a));

        return { lista: maisBarata, origem: 'automatica' };
    }

    if (!priorizarMenorPreco) {
        return { lista: base, origem: origemDaBase(base, idListaCabecalho) };
    }

    // Só PROMOCIONAIS entram na comparação: lista por segmento ou por vendedor não é promoção, e
    // deixá-la competir faria o preço do cliente A vazar para o documento do cliente B.
    let escolhida = base;

    for (const promocional of candidatas) {
        if (!promocional.isPromocao) continue;
        if (promocional.idListaPreco === base.idListaPreco) continue;

        // `<` estrito: no empate prevalece a do cabeçalho, como manda o desempate da §6.2.2.3.
        if (promocional.preco < escolhida.preco) escolhida = promocional;
    }

    return {
        lista: escolhida,
        origem: escolhida.idListaPreco === base.idListaPreco
            ? origemDaBase(base, idListaCabecalho)
            : 'automatica',
    };
}

/**
 * A origem quando venceu a BASE da resolução — e não uma promocional.
 *
 * Base é a lista do cabeçalho quando o documento tem uma, e a padrão quando não tem. Dizer
 * "lista do cabeçalho" num documento sem lista escolhida seria mentir sobre de onde veio o preço.
 */
function origemDaBase(base: ProdutoListaPrecoVigenteStruct, idListaCabecalho: number | null): OrigemPreco {
    return idListaCabecalho && base.idListaPreco === idListaCabecalho ? 'cabecalho' : 'padrao';
}

/**
 * A origem do preço que uma LINHA já gravada carrega — a leitura inversa do que a resolução fez.
 *
 * Existe para que o rastreio da lista e o texto do popover partam do MESMO critério. Escrever a
 * comparação nos dois lugares já produziu o mesmo defeito duas vezes: com o documento sem lista
 * no cabeçalho, `idListaCabecalho` é nulo e QUALQUER lista parecia "vinda de outro lugar".
 */
export function origemDaLinha(
    lista: ProdutoListaPrecoVigenteStruct | null,
    idListaCabecalho: number | null,
    isListaPrecoManual: boolean,
): OrigemPreco {
    if (!lista) return 'sem-lista';
    if (isListaPrecoManual) return 'manual';

    const ehEsperada = idListaCabecalho ? lista.idListaPreco === idListaCabecalho : lista.isPadrao;

    return ehEsperada ? origemDaBase(lista, idListaCabecalho) : 'automatica';
}

/**
 * A frase que o popover do item mostra acima do select, dizendo de onde veio o preço atual.
 *
 * O texto é o da §6.2.5 — e o caso `manual` é o único que sobrevive a uma troca de lista no
 * cabeçalho, por isso ele avisa que prevalece.
 */
export function textoOrigemDoPreco(origem: OrigemPreco, nomeLista: string | null): string {
    switch (origem) {
        case 'manual':
            return `Escolha manual${nomeLista ? ` — lista ${nomeLista}` : ''}. Prevalece sobre a regra automática.`;
        case 'automatica':
            return `Aplicada automaticamente${nomeLista ? `: lista ${nomeLista}` : ''} — menor preço entre a lista do cabeçalho e as promocionais vigentes.`;
        case 'cabecalho':
            return `Aplicada automaticamente${nomeLista ? `: lista ${nomeLista}` : ''} — lista do cabeçalho do documento.`;
        case 'padrao':
            return `Aplicada automaticamente${nomeLista ? `: lista ${nomeLista}` : ''} — o documento não tem lista de preço no cabeçalho.`;
        default:
            return 'Produto sem preço em lista vigente.';
    }
}

/** O "vigente até {data}" do rastreio. Lista sem prazo não ganha sufixo nenhum. */
export function textoVigencia(lista: ProdutoListaPrecoVigenteStruct | null): string {
    if (!lista) return '';

    if (!lista.dataTerminoVigencia) return `Lista ${lista.descricao}`;

    const fim = new Date(lista.dataTerminoVigencia);

    return `Lista ${lista.descricao} — vigente até ${fim.toLocaleDateString('pt-BR')}`;
}
